import { Button, Input } from "antd";
import { useCallback } from "react";
import {
  Controller,
  ControllerProps,
  SubmitHandler,
  useForm,
} from "react-hook-form";

const emailPattern =
  /^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$/i;

export type MagicLinkLoginInputs = {
  email: string;
};

export type MagicLinkLoginFormProps = {
  onLogin: (args: MagicLinkLoginInputs) => void;
};

export const MagicLinkLoginForm: React.FC<MagicLinkLoginFormProps> = ({
  onLogin,
}) => {
  const { control, handleSubmit } = useForm<MagicLinkLoginInputs>();
  const handleLoginForm: SubmitHandler<MagicLinkLoginInputs> = (data) => {
    onLogin(data);
  };

  const renderEmail = useCallback<
    ControllerProps<MagicLinkLoginInputs, "email">["render"]
  >(
    ({ field }) => <Input {...field} placeholder="Your organization email" />,
    []
  );

  return (
    <form
      onSubmit={handleSubmit(handleLoginForm)}
      style={{ textAlign: "center" }}
    >
      <Controller
        name="email"
        control={control}
        rules={{
          required: true,
          pattern: emailPattern,
        }}
        render={renderEmail}
      />
      <Button type="primary" htmlType="submit" style={{ marginTop: "1em" }}>
        Login
      </Button>
    </form>
  );
};

export type PasswordFormProps = {
  showForm: PasswordFormType;
  setShowForm: (type: PasswordFormType) => void;
  onLogin: (args: PasswordLoginInputs) => void;
  onSignUp: (args: PasswordSignUpInputs) => void;
  onForgotPassword: (args: ForgotPasswordInputs) => void;
};

export type PasswordFormType = "forgot" | "login" | "signup";

export const PasswordForm: React.FC<PasswordFormProps> = ({
  showForm,
  setShowForm,
  onLogin,
  onSignUp,
  onForgotPassword,
}) => {
  const handleChangeForm = useCallback(
    (type: PasswordFormType) => {
      return () => setShowForm(type);
    },
    [setShowForm]
  );

  if (showForm === "login") {
    return (
      <PasswordLoginForm
        onLogin={onLogin}
        onSignUpClick={handleChangeForm("signup")}
        onForgotPasswordClick={handleChangeForm("forgot")}
      />
    );
  }
  if (showForm === "signup") {
    return (
      <PasswordSignUpForm
        onSignUp={onSignUp}
        onLoginClick={handleChangeForm("login")}
      />
    );
  }
  if (showForm === "forgot") {
    return (
      <ForgotPasswordForm
        onForgotPassword={onForgotPassword}
        onLoginClick={handleChangeForm("login")}
      />
    );
  }

  return <div>Unknown form</div>;
};

export type PasswordLoginInputs = {
  email: string;
  password: string;
};

export type PasswordLoginFormProps = {
  onLogin: (args: PasswordLoginInputs) => void;
  onSignUpClick: () => void;
  onForgotPasswordClick: () => void;
};

export const PasswordLoginForm: React.FC<PasswordLoginFormProps> = ({
  onLogin,
  onSignUpClick,
  onForgotPasswordClick,
}) => {
  const { control, handleSubmit } = useForm<PasswordLoginInputs>({
    mode: "onChange",
  });

  const handleLoginForm: SubmitHandler<PasswordLoginInputs> = (data) => {
    onLogin(data);
  };

  const renderEmail = useCallback<
    ControllerProps<PasswordLoginInputs, "email">["render"]
  >(
    ({ field }) => <Input {...field} placeholder="Your organization email" />,
    []
  );

  const renderPassword = useCallback<
    ControllerProps<PasswordLoginInputs, "password">["render"]
  >(
    ({ field }) => (
      <Input {...field} placeholder="Your password" type="password" />
    ),
    []
  );

  return (
    <form
      onSubmit={handleSubmit(handleLoginForm)}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "1rem",
        flexDirection: "column",
      }}
    >
      <div style={{ width: "100%" }}>
        <div>Email</div>
        <Controller
          name="email"
          control={control}
          rules={{
            required: true,
            pattern: emailPattern,
          }}
          render={renderEmail}
        />
      </div>

      <div
        style={{
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: "1rem",
          }}
        >
          <div>Password</div>
          <Button
            type="link"
            tabIndex={-1}
            onClick={onForgotPasswordClick}
            style={{
              padding: 0,
              margin: 0,
              marginBottom: "-6px",
            }}
          >
            Forgot password?
          </Button>
        </div>
        <Controller
          name="password"
          control={control}
          rules={{
            required: true,
          }}
          render={renderPassword}
        />
      </div>
      <div
        style={{
          display: "flex",
          gap: "1rem",
          width: "100%",
        }}
      >
        <Button type="primary" variant="filled" htmlType="submit">
          Login
        </Button>
        <Button variant="outlined" onClick={onSignUpClick}>
          Create an account
        </Button>
      </div>
    </form>
  );
};

export type PasswordSignUpInputs = {
  email: string;
  password: string;
  confirm: string;
};

export type PasswordSignUpFormProps = {
  onSignUp: (args: PasswordSignUpInputs) => void;
  onLoginClick: () => void;
};

export const PasswordSignUpForm: React.FC<PasswordSignUpFormProps> = ({
  onSignUp,
  onLoginClick,
}) => {
  const renderEmail = useCallback<
    ControllerProps<PasswordSignUpInputs, "email">["render"]
  >(
    ({ field }) => <Input {...field} placeholder="Your organization email" />,
    []
  );

  const renderPassword = useCallback<
    ControllerProps<PasswordSignUpInputs, "password">["render"]
  >(
    ({ field }) => (
      <Input {...field} placeholder="Your password" type="password" />
    ),
    []
  );
  const renderConfirm = useCallback<
    ControllerProps<PasswordSignUpInputs, "confirm">["render"]
  >(
    ({ field }) => (
      <Input {...field} placeholder="Confirm your password" type="password" />
    ),
    []
  );
  const { control, handleSubmit } = useForm<PasswordSignUpInputs>();
  const handleSignUpForm: SubmitHandler<PasswordSignUpInputs> = (data) => {
    onSignUp(data);
  };

  return (
    <form
      onSubmit={handleSubmit(handleSignUpForm)}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "1rem",
        flexDirection: "column",
      }}
    >
      <div style={{ width: "100%" }}>
        <div>Email</div>
        <Controller
          name="email"
          control={control}
          rules={{
            required: true,
            pattern: emailPattern,
          }}
          render={renderEmail}
        />
      </div>

      <div
        style={{
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-baseline",
            gap: "1rem",
          }}
        >
          <div>Password</div>
        </div>
        <Controller
          name="password"
          control={control}
          rules={{
            required: true,
          }}
          render={renderPassword}
        />
      </div>

      <div
        style={{
          width: "100%",
        }}
      >
        <div>Confirm Password</div>
        <Controller
          name="confirm"
          control={control}
          rules={{
            required: true,
          }}
          render={renderConfirm}
        />
      </div>

      <div
        style={{
          display: "flex",
          gap: "1rem",
          width: "100%",
        }}
      >
        <Button type="primary" variant="filled" htmlType="submit">
          Sign up
        </Button>
        <Button variant="outlined" htmlType="button" onClick={onLoginClick}>
          Back to login
        </Button>
      </div>
    </form>
  );
};

export type ForgotPasswordInputs = {
  email: string;
};
export type ForgotPasswordFormProps = {
  onForgotPassword: (args: ForgotPasswordInputs) => void;
  onLoginClick: () => void;
};
export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onForgotPassword,
  onLoginClick,
}) => {
  const { control, handleSubmit } = useForm<ForgotPasswordInputs>();
  const handleForgotPasswordForm: SubmitHandler<ForgotPasswordInputs> = (
    data
  ) => {
    onForgotPassword(data);
  };

  const renderEmail = useCallback<
    ControllerProps<ForgotPasswordInputs, "email">["render"]
  >(
    ({ field }) => <Input {...field} placeholder="Your organization email" />,
    []
  );

  return (
    <form
      onSubmit={handleSubmit(handleForgotPasswordForm)}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "1rem",
        width: "100%",
      }}
    >
      <Controller
        name="email"
        control={control}
        rules={{
          required: true,
          pattern: emailPattern,
        }}
        render={renderEmail}
      />
      <div
        style={{
          display: "flex",
          gap: "1rem",
          width: "100%",
        }}
      >
        <Button type="primary" htmlType="submit">
          Send password reset email
        </Button>
        <Button variant="outlined" htmlType="button" onClick={onLoginClick}>
          Back to login
        </Button>
      </div>
    </form>
  );
};
